/* eslint-disable @typescript-eslint/dot-notation */
import { ChangeDetectionStrategy, Component, signal, ViewEncapsulation } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';

import { DateFormatEnum } from '../../models/date-format.enum';
import { DateInputEnum } from '../../models/date-input.enum';
import { isoToMaskModel, maskModelToIso } from '../../utils/dates-utils';
import { MaskedDateCellEditorInlineComponent } from './masked-date-cell-editor-inline/masked-date-cell-editor-inline.component';
import { MaskedDateCellEditorPopupComponent } from './masked-date-cell-editor-popup/masked-date-cell-editor-popup.component';

export interface IMaskedDateCellEditorParams {
    required?: boolean;
    inlineEditor?: boolean;
    inputType?: DateInputEnum;
    inputFormat?: DateFormatEnum;
    dateInputFormat?: string;
    timeInputFormat?: string;
    dateTimeInputFormat?: string;
    dateSeparator?: string;
    timeSeparator?: string;
}

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'se-masked-date-cell-editor',
    templateUrl: './masked-date-cell-editor.component.html',
    styleUrls: ['./masked-date-cell-editor.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [ReactiveFormsModule, MaskedDateCellEditorInlineComponent, MaskedDateCellEditorPopupComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaskedDateCellEditorComponent implements ICellEditorAngularComp {
    readonly editInline = signal<boolean>(false);
    readonly inputType = signal<DateInputEnum | null>(null);
    readonly inputFormat = signal<DateFormatEnum | null>(null);
    readonly dateTimeInputFormat = signal<string>('');
    readonly dateInputFormat = signal<string>('');
    readonly timeInputFormat = signal<string>('');
    readonly dateSeparator = signal<string>('');
    readonly timeSeparator = signal<string>('');
    readonly cellWidth = signal<string>('');
    readonly cellReady = signal<boolean>(false);

    readonly formControl = new FormControl<string | null>(null);

    /** ISO originale (UTC instant) come stringa */
    private originalValueIso: string | null = null;

    agInit(params: ICellEditorParams & IMaskedDateCellEditorParams): void {
        const inputType = params.inputType ?? DateInputEnum.DATE;
        const inputFormat = params.inputFormat ?? DateFormatEnum.SHORT;

        this.inputType.set(inputType);
        this.inputFormat.set(inputFormat);
        this.editInline.set(params.inlineEditor !== false);

        this.dateInputFormat.set(params.dateInputFormat ?? 'DDMMYYYY');
        this.timeInputFormat.set(params.timeInputFormat ?? 'HH:mm');
        this.dateTimeInputFormat.set(params.dateTimeInputFormat ?? 'DDMMYYYY HH:mm');
        this.dateSeparator.set(params.dateSeparator ?? '/');
        this.timeSeparator.set(params.timeSeparator ?? ':');

        this.cellWidth.set(params.column.getActualWidth() + 'px');

        if (params.required) this.formControl.addValidators(Validators.required);

        // ✅ origine: preferisci la stringa ISO se è quella che la grid ti passa
        this.originalValueIso = typeof params.value === 'string' ? params.value : null;

        // ✅ valore iniziale UI (locale)
        const initialModel = isoToMaskModel(this.originalValueIso, inputType);
        this.formControl.setValue(initialModel, { emitEvent: false });
        this.formControl.updateValueAndValidity({ emitEvent: false });
    }

    getValue(): string | null {
        const model = this.formControl.getRawValue();
        if (!model || model.trim() === '') return null;

        const type = this.inputType() ?? DateInputEnum.DATE;
        return maskModelToIso(model, type, this.originalValueIso);
    }

    isPopup(): boolean {
        return !this.editInline();
    }

    afterGuiAttached(): void {
        this.cellReady.set(true);
    }
}
